import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@portal/ui';
import { useMe } from '@portal/web-sdk';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandPalette } from './CommandPalette';
import { findTrail, useNavigation } from './navigation';
import { rememberPage } from './recent';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const COLLAPSED_KEY = 'portal.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Layout shared by every module page: sidebar (rail on desktop, drawer on mobile), top bar, content. */
export function AppShell() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const accountLocale = me?.user.locale;

  // The language saved on the account wins over this browser's last choice.
  useEffect(() => {
    if (accountLocale && accountLocale !== i18n.resolvedLanguage)
      void i18n.changeLanguage(accountLocale);
  }, [accountLocale, i18n]);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigation();
  const currentNavId = findTrail(nav.data?.items ?? [], pathname).at(-1)?.id;
  useEffect(() => {
    if (currentNavId) rememberPage(currentNavId);
  }, [currentNavId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, value ? '0' : '1');
      } catch {
        // Not persisted when storage is unavailable.
      }
      return !value;
    });
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <aside
        className={`hidden shrink-0 border-r border-sidebar-border transition-[width] duration-200 md:block ${collapsed ? 'w-14' : 'w-60'}`}
      >
        <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </aside>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SheetTitle className="sr-only">{t('app.name')}</SheetTitle>
          <SheetDescription className="sr-only">{t('shell.openMenu')}</SheetDescription>
          <Sidebar collapsed={false} onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenCommand={() => setCommandOpen(true)}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <main key={pathname} className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  );
}
